<template>
  <v-dialog
    v-model="isDialogVisible"
    :fullscreen="isMobile"
    :max-width="isMobile ? '100%' : '500px'"
    transition="dialog-bottom-transition"
    :overlay-color="'#000000'"
    :overlay-opacity="0.8"
    content-class="popup-dialog"
    persistent>
    <v-card
      v-if="popupsToShow.length > 0 && popupsToShow[currentIndex]"
      class="d-flex flex-column pa-0"
      :class="{ 'fill-height': isMobile }"
      flat
      tile
      color="#1c1c1e">
      <div v-if="popupsToShow.length > 1" class="popup-counter">{{ currentIndex + 1 }} / {{ popupsToShow.length }}</div>

      <v-btn icon @click="closeDialog" class="popup-close-btn">
        <v-icon>mdi-close</v-icon>
      </v-btn>

      <v-carousel
        v-model="currentIndex"
        :show-arrows="!isMobile && popupsToShow.length > 1"
        show-arrows-on-hover
        :continuous="false"
        height="100%"
        :hide-delimiters="popupsToShow.length <= 1"
        class="popup-carousel">
        <template v-slot:prev="{ on, attrs }">
          <v-btn v-bind="attrs" v-on="on" fab small icon class="carousel-nav-btn">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
        </template>
        <template v-slot:next="{ on, attrs }">
          <v-btn v-bind="attrs" v-on="on" fab small icon class="carousel-nav-btn">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </template>

        <v-carousel-item v-for="popup in popupsToShow" :key="popup._id">
          <div class="popup-item d-flex flex-column">
            <v-img
              v-if="popup.image && popup.image.length > 2"
              :src="'https://app.wellcomefit.ch/img/' + popup.image"
              :alt="popup.title"
              :aspect-ratio="isMobile ? 16 / 9 : 16 / 9"
              cover
              class="flex-shrink-0 popup-image">
              <template v-slot:placeholder>
                <div class="fill-height d-flex justify-center align-center">
                  <v-progress-circular indeterminate color="grey-lighten-5"></v-progress-circular>
                </div>
              </template>
            </v-img>
            <div v-else class="no-image-placeholder d-flex align-center justify-center flex-shrink-0">
              <v-img
                :src="`https://app.wellcomefit.ch/img/logos/studio/WELL-COME-FIT_LOGO_${selectedStudio?.code.toUpperCase()}_BLACK.png`"
                :alt="popup.title"
                :aspect-ratio="isMobile ? 16 / 9 : 16 / 9"
                contain
                height="150"
                class="flex-shrink-0">
                <template v-slot:placeholder>
                  <div class="fill-height d-flex justify-center align-center">
                    <v-progress-circular indeterminate color="grey-lighten-5"></v-progress-circular>
                  </div>
                </template>
              </v-img>
            </div>

            <div class="pa-4 text-white overflow-y-auto" style="flex-grow: 1">
              <h3 class="text-h6 font-weight-bold mb-2">{{ popup.title }}</h3>

              <div v-if="!expandedState[popup._id]" class="popup-text-content text-body-2">
                <div v-html="getShortText(popup.text)"></div>
              </div>

              <v-expand-transition>
                <div v-if="expandedState[popup._id]" class="popup-text-content text-body-1">
                  <div v-html="popup.text"></div>
                </div>
              </v-expand-transition>
            </div>

            <v-card-actions v-if="!isMobile" class="popup-actions d-flex flex-column align-center justify-center pa-3">
              <v-btn block text @click="toggleExpanded(popup._id)">
                {{ expandedState[popup._id] ? "Weniger anzeigen" : "Mehr anzeigen" }}
              </v-btn>
            </v-card-actions>
          </div>
        </v-carousel-item>
      </v-carousel>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/api"
import { mapActions, mapState } from "vuex"

export default {
  name: "PopupDialog",
  props: {
    // <-- HIER HINZUFÜGEN
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      allPopups: [],
      popupsToShow: [],
      isDialogVisible: false,
      currentIndex: 0,
      expandedState: {}, // Verwaltet den "expanded"-Zustand für JEDES Popup
    }
  },
  computed: {
    ...mapState("studioStore", ["selectedStudio"]),
    isMobile() {
      // $vuetify.breakpoint ist der Standard in Vuetify 2
      return this.$vuetify.breakpoint.smAndDown
    },
  },
  watch: {
    disabled(isNowDisabled) {
      if (isNowDisabled && this.isDialogVisible) {
        this.closeDialog()
      }
    },
    currentIndex(newIndex) {
      if (this.isDialogVisible && this.popupsToShow.length > newIndex) {
        this.markPopupAsShown(this.popupsToShow[newIndex]._id)
      }
    },
    isDialogVisible(isActive) {
      if (isActive && this.popupsToShow.length > 0) {
        this.markPopupAsShown(this.popupsToShow[0]._id)
      }
    },
  },
  methods: {
    // Vuex Action einbinden
    ...mapActions("navigationStoreNotPersisted", ["setNavigationbarVisibility"]),

    async fetchAndShowPopups() {
      try {
        const popups = await api.PopupService.getActive()
        this.allPopups = popups
        this.cleanupLocalStorage()
        this.preparePopups()
      } catch (e) {
        console.error("Fehler beim Abrufen der Popups:", e)
      }
    },
    preparePopups() {
      const cooldown = 7 * 24 * 60 * 60 * 1000 // 24 Stunden
      const now = new Date().getTime()

      const eligiblePopups = this.allPopups.filter((popup) => {
        const lastShownKey = `popup_last_shown_${popup._id}`
        const lastShown = localStorage.getItem(lastShownKey)
        return !lastShown || now - parseInt(lastShown) > cooldown
      })

      if (eligiblePopups.length > 0) {
        this.popupsToShow = eligiblePopups.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

        const initialState = {}
        this.popupsToShow.forEach((popup) => {
          initialState[popup._id] = this.isMobile
        })
        this.expandedState = initialState

        setTimeout(() => {
          if (this.disabled) {
            return
          }

          this.setNavigationbarVisibility(false) // Navbar ausblenden
          this.currentIndex = 0
          this.isDialogVisible = true
        }, 3000)
      }
    },
    toggleExpanded(popupId) {
      this.$set(this.expandedState, popupId, !this.expandedState[popupId])
    },
    markPopupAsShown(popupId) {
      if (!popupId) return
      localStorage.setItem(`popup_last_shown_${popupId}`, new Date().getTime().toString())
    },
    getShortText(html) {
      if (!html) return ""
      const maxLength = 120
      const tempDiv = document.createElement("div")
      tempDiv.innerHTML = html
      const plainText = tempDiv.textContent || tempDiv.innerText || ""
      return plainText.length > maxLength ? plainText.substring(0, maxLength) + "&hellip;" : plainText
    },
    cleanupLocalStorage() {
      const now = new Date().getTime()
      const cooldown = 24 * 60 * 60 * 1000 // 24 Stunden
      const currentIds = this.allPopups.map((p) => p._id)
      const keysToRemove = []

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)

        // Nur Schlüssel prüfen, die zu unseren Popups gehören
        if (key && key.startsWith("popup_last_shown_")) {
          const popupIdFromKey = key.replace("popup_last_shown_", "")
          const lastShown = parseInt(localStorage.getItem(key) || "0")

          // BEDINGUNG 1: Der Popup-Eintrag ist obsolet (ID existiert nicht mehr in der API-Antwort)
          const isObsolete = !currentIds.includes(popupIdFromKey)

          // BEDINGUNG 2: Der Zeitstempel ist abgelaufen (älter als die Cooldown-Periode)
          const isExpired = now - lastShown > cooldown

          // Wenn eine der beiden Bedingungen zutrifft, kann der Schlüssel weg
          if (isObsolete || isExpired) {
            keysToRemove.push(key)
          }
        }
      }

      // Alle markierten Schlüssel auf einmal entfernen
      if (keysToRemove.length > 0) {
        console.log(`Bereinige ${keysToRemove.length} veraltete/abgelaufene Popup-Einträge...`)
        keysToRemove.forEach((key) => {
          localStorage.removeItem(key)
        })
      }
    },
    closeDialog() {
      if (this.popupsToShow.length > 0) {
        this.popupsToShow.forEach((popup) => {
          this.markPopupAsShown(popup._id)
        })
      }
      this.setNavigationbarVisibility(true) // Navbar wieder einblenden
      this.isDialogVisible = false
      this.expandedState = {}
    },
  },
  created() {
    this.fetchAndShowPopups()
  },
}
</script>

<style lang="scss" scoped>
::v-deep .popup-dialog {
  margin: 0 !important;
  height: 100%;
  width: 100%;
  align-self: flex-end;
  @media (min-width: 768px) {
    align-self: center;
    border-radius: 16px !important;
    height: auto;
    max-height: 750px;
  }
}
::v-deep .popup-dialog .v-card {
  border-radius: 0 !important;
  @media (min-width: 768px) {
    border-radius: 16px !important;
  }
}

.popup-item {
  background-color: #1c1c1e;
  color: white;
}

::v-deep .v-carousel__controls {
  background-color: transparent !important;
}

.no-image-placeholder {
  height: 200px;
  background-color: #333;
}

.popup-text-content {
  line-height: 1.5;
  word-wrap: break-word;
  color: #e0e0e0 !important;

  ::v-deep a {
    color: #87cefa !important;
    text-decoration: underline !important;
  }
}

.popup-counter {
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 20;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.popup-close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 20;
  color: white !important;
  background-color: rgba(0, 0, 0, 0.4);
}

.carousel-nav-btn {
  /* Nur die Farbe für einen schönen Look behalten */
  background-color: rgba(49, 49, 49, 0.6) !important;
  color: white !important;
}

::v-deep .v-carousel__controls {
  bottom: 50px !important;
  background: rgba(0, 0, 0, 0.2);
  padding-top: 5px;
  border-radius: 10px;

  .v-carousel__indicator .v-icon {
    color: rgba(255, 255, 255, 0.5) !important;
  }
  .v-carousel__indicator--active .v-icon {
    color: white !important;
  }
}

.popup-actions {
  background-color: rgba(0, 0, 0, 0.3);
  border-top: 1px solid #444;
  min-height: 52px;

  .v-btn {
    color: #bdbdbd !important;
    font-weight: bold;
  }
}

.popup-image {
  width: 100%; /* Stellt sicher, dass die Breite als Basis dient */
  aspect-ratio: 16 / 9; /* Erzwingt das Seitenverhältnis */
  object-fit: cover; /* Sorgt dafür, dass das Bild den Raum füllt, ohne sich zu verzerren */
}
</style>
